// src/pages/admin/ReportLearnerPage.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    User, Users, Search, RefreshCcw, ChevronRight, ArrowLeft,
    BookOpen, BarChart2, Trophy, Clock, CheckCircle2, XCircle,
    AlertCircle, GraduationCap, TrendingUp, FileText, Loader2,
    Shield, Calendar, Eye, Check, X, HelpCircle, Timer, Printer
} from 'lucide-react'
import DataTable from '@/widgets/DataTable'
import { getLearners, getReportLearnerOverview, getReportLearnerCourse, getReportLearnerSubmission } from '@/api/kuizy'
import { formatDateTime } from '@/settings/master'

// ─── helpers ───────────────────────────────────────────────
const pct = v => (v != null && !Number.isNaN(Number(v))) ? `${Number(v).toFixed(1)}%` : '-'
const fmtScore = (s, f) => (s != null && f != null) ? `${s}/${f}` : '-'
const fmtTime = sec => {
    if (sec == null) return '-'
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return m > 0 ? `${m} นาที ${s} วินาที` : `${s} วินาที`
}

const Avatar = ({ src, name, size = 'md' }) => {
    const dim = size === 'lg' ? 'w-16 h-16 text-2xl' : size === 'sm' ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base'
    const letter = (name || '?')[0].toUpperCase()
    if (src) return <img src={src} alt={name} className={`${dim} rounded-full object-cover ring-2 ring-white shadow`} />
    return (
        <span className={`${dim} rounded-full flex items-center justify-center bg-gradient-to-br from-primary to-secondary text-white font-bold ring-2 ring-white shadow shrink-0`}>
            {letter}
        </span>
    )
}

const ScoreBadge = ({ pct: p }) => {
    if (p == null) return <span className="text-gray-400 text-sm">-</span>
    const v = Number(p)
    const cls = v >= 80 ? 'bg-green-100 text-green-700' : v >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{v.toFixed(1)}%</span>
}

const PassBadge = ({ passed }) =>
    passed
        ? <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><CheckCircle2 size={12} />ผ่าน</span>
        : <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full"><XCircle size={12} />ไม่ผ่าน</span>

const ProgressBar = ({ value, max = 100, color = 'bg-primary' }) => {
    const w = max > 0 ? Math.min(100, (value / max) * 100) : 0
    return (
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${w}%` }} />
        </div>
    )
}

// ─── Layer 0: Learner List ──────────────────────────────────
function LearnerList({ onSelect }) {
    const [rows, setRows] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(20)
    const [sortBy, setSortBy] = useState('created_at')
    const [sortDir, setSortDir] = useState('desc')
    const [status, setStatus] = useState(1)

    const fetch = useCallback(() => {
        setLoading(true)
        getLearners({ searchText: search || undefined, page, per_page: perPage, sort_by: sortBy, sort_dir: sortDir, status })
            .then(r => { const d = r.data || {}; setRows(d.rows || []); setTotal(d.total || 0) })
            .catch(() => { setRows([]); setTotal(0) })
            .finally(() => setLoading(false))
    }, [search, page, perPage, sortBy, sortDir, status])

    useEffect(() => { fetch() }, [fetch])

    const columns = useMemo(() => [
        { key: '__no', title: '#', sortable: false, render: (_, i) => (page - 1) * perPage + i + 1 },
        {
            key: 'full_name', title: 'ผู้เรียน', sortable: true, sortKey: 'first_name',
            render: r => (
                <div className="flex items-center gap-2">
                    <Avatar src={r.avatar_url} name={r.first_name || r.display_name || r.username} size="sm" />
                    <div>
                        <div className="font-medium text-gray-900 text-sm">{r.first_name} {r.last_name}</div>
                        {r.nick_name && <div className="text-xs text-gray-400">({r.nick_name})</div>}
                    </div>
                </div>
            )
        },
        { key: 'email', title: 'อีเมล', sortable: true, render: r => <span className="text-sm text-gray-600">{r.email || '-'}</span> },
        {
            key: 'class_names',
            title: 'ห้องเรียน',
            sortable: true,
            render: r =>
                Array.isArray(r.class_names) && r.class_names.length
                    ? r.class_names.join(', ')
                    : '-'
        },
        {
            key: 'group_names',
            title: 'กลุ่ม',
            sortable: true,
            render: r =>
                Array.isArray(r.group_names) && r.group_names.length
                    ? r.group_names.join(', ')
                    : '-'
        },
        {
            key: 'status', title: 'สถานะ', align: 'center', sortable: true,
            render: r => r.status === 1
                ? <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">ใช้งาน</span>
                : r.status === 0
                    ? <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">ยังไม่ใช้งาน</span>
                    : <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">-</span>
        },
        // { key: 'created_at', title: 'สมัครเมื่อ', align: 'right', sortable: true, render: r => <span className="text-xs text-gray-500">{formatDateTime(r.created_at)}</span> },
        {
            key: '__actions', title: 'รายงาน', align: 'center', sortable: false,
            render: r => (
                <button
                    onClick={() => onSelect(r)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-secondary transition-colors"
                >
                    <BarChart2 size={13} /> ดูรายงาน
                </button>
            )
        }
    ], [page, perPage, onSelect])

    return (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex flex-col gap-5">
            {/* Header */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-secondary/10 to-purple-50 border border-white/40 p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 w-12 h-12 rounded-2xl bg-white shadow grid place-items-center">
                            <GraduationCap className="text-primary" size={22} />
                        </div>
                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">รายงานรายบุคคล</h1>
                            <p className="text-sm text-gray-500 mt-0.5">เลือกผู้เรียนเพื่อดูรายงานความก้าวหน้าและผลสอบ</p>
                        </div>
                    </div>
                    <button
                        onClick={fetch}
                        className="inline-flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl px-3 py-2 text-sm self-start sm:self-auto"
                    >
                        <RefreshCcw size={15} /> โหลดใหม่
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
                    <label className="flex items-center gap-1 text-sm font-medium text-gray-700"><Search size={14} /> ค้นหาผู้เรียน</label>
                    <div className="relative">
                        <input
                            placeholder="ชื่อ / อีเมล / ห้องเรียน / กลุ่ม"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1) } }}
                            className="border border-gray-300 rounded-lg pl-9 pr-3 py-2 w-full text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                </div>
                <button
                    onClick={() => { setSearch(searchInput); setPage(1) }}
                    className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 text-sm font-semibold"
                >
                    <Search size={14} /> ค้นหา
                </button>
                {search && (
                    <button
                        onClick={() => { setSearchInput(''); setSearch(''); setPage(1) }}
                        className="inline-flex items-center gap-1 border border-gray-300 text-gray-600 rounded-lg px-3 py-2 text-sm hover:bg-gray-50"
                    >
                        <XCircle size={14} /> ล้าง
                    </button>
                )}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 text-sm text-gray-600">
                    <Users size={15} /> ผู้เรียนทั้งหมด <span className="font-semibold text-gray-900">{total}</span> คน
                </div>
                <DataTable
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    total={total}
                    page={page}
                    perPage={perPage}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onPageChange={setPage}
                    onPerPageChange={n => { setPerPage(n); setPage(1) }}
                    onSortChange={(k, d) => { setSortBy(k); setSortDir(d); setPage(1) }}
                />
            </div>
        </motion.div>
    )
}

// ─── Layer 1: Learner Overview ──────────────────────────────
function LearnerOverview({ learner, onSelectCourse, onBack }) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        setLoading(true); setError(null)
        getReportLearnerOverview(learner.id)
            .then(r => setData(r.data || {}))
            .catch(() => setError('โหลดข้อมูลไม่สำเร็จ'))
            .finally(() => setLoading(false))
    }, [learner.id])

    const info = data?.learner || learner
    const courses = data?.courses || []

    return (
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="flex flex-col gap-5">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors">
                    <ArrowLeft size={15} /> รายชื่อผู้เรียน
                </button>
                <ChevronRight size={14} className="text-gray-300" />
                <span className="text-gray-800 font-medium">{info.display_name || `${info.first_name} ${info.last_name}`}</span>
            </div>

            {/* Profile Card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <Avatar src={info.avatar_url} name={info.display_name || info.first_name} size="lg" />
                    <div className="flex-1">
                        <div className="text-xl font-semibold text-gray-900">
                            {info.display_name || `${info.first_name || ''} ${info.last_name || ''}`.trim() || info.username}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                            {info.email && <span className="flex items-center gap-1"><User size={13} />{info.email}</span>}
                            {info.job_title && <span className="flex items-center gap-1"><Shield size={13} />{info.job_title}</span>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <BookOpen size={15} className="text-primary" />
                        <span><span className="font-semibold text-gray-900">{courses.length}</span> หลักสูตร</span>
                    </div>
                </div>
            </div>

            {/* Course Cards */}
            {loading ? (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                            <div className="h-36 bg-gray-200" />
                            <div className="p-4 space-y-3">
                                <div className="h-4 bg-gray-200 rounded w-3/4" />
                                <div className="h-3 bg-gray-100 rounded w-1/2" />
                                <div className="h-3 bg-gray-100 rounded w-full" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-2 text-red-600">
                    <AlertCircle size={18} /> {error}
                </div>
            ) : courses.length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-500">
                    <BookOpen size={36} className="mx-auto mb-2 opacity-30" />
                    ไม่พบหลักสูตรที่ลงทะเบียน
                </div>
            ) : (
                <>
                    <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                        <BookOpen size={17} className="text-primary" /> หลักสูตรที่ลงทะเบียน
                    </h2>
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                        {courses.map(c => (
                            <CourseCard key={c.course_id} course={c} onClick={() => onSelectCourse(c)} />
                        ))}
                    </div>
                </>
            )}
        </motion.div>
    )
}

function CourseCard({ course: c, onClick }) {
    const attempted = c.tests_attempted ?? 0
    const total = c.test_count ?? 0
    const prog = total > 0 ? (attempted / total) * 100 : 0

    return (
        <motion.button
            onClick={onClick}
            whileHover={{ y: -2 }}
            transition={{ duration: 0.15 }}
            className="group text-left rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-lg overflow-hidden transition-shadow w-full"
        >
            {/* Image */}
            <div className="relative h-36 overflow-hidden bg-gradient-to-br from-primary/20 to-secondary/20">
                {c.image_url ? (
                    <img src={c.image_url} alt={c.course_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center opacity-40">
                        <BookOpen size={44} className="text-primary" />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                {c.course_code && (
                    <span className="absolute top-2.5 left-2.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/50 text-white backdrop-blur-sm">
                        {c.course_code}
                    </span>
                )}
                <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5">
                    <div className="font-semibold text-sm text-white line-clamp-2 drop-shadow leading-tight">{c.course_name}</div>
                </div>
            </div>

            {/* Body */}
            <div className="p-3 space-y-2.5">
                {/* Stats grid 2x2 */}
                <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-50 rounded-lg px-2.5 py-2 text-center">
                        <div className="text-[10px] text-gray-500 flex items-center justify-center gap-1"><FileText size={10} />สอบแล้ว</div>
                        <div className="font-bold text-base text-gray-800 mt-0.5">{attempted}<span className="text-xs font-normal text-gray-400">/{total} ชุด</span></div>
                    </div>
                    <div className="bg-gray-50 rounded-lg px-2.5 py-2 text-center">
                        <div className="text-[10px] text-gray-500 flex items-center justify-center gap-1"><CheckCircle2 size={10} />สอบผ่าน</div>
                        <div className="font-bold text-base text-green-600 mt-0.5">{c.passed_tests ?? 0}<span className="text-xs font-normal text-gray-400"> ชุด</span></div>
                    </div>
                    <div className="bg-gray-50 rounded-lg px-2.5 py-2 text-center">
                        <div className="text-[10px] text-gray-500 flex items-center justify-center gap-1"><Clock size={10} />จำนวนครั้งที่สอบ</div>
                        <div className="font-bold text-base text-gray-800 mt-0.5">{c.attempt_count ?? 0}<span className="text-xs font-normal text-gray-400"> ครั้ง</span></div>
                    </div>
                    <div className="bg-gray-50 rounded-lg px-2.5 py-2 text-center">
                        <div className="text-[10px] text-gray-500 flex items-center justify-center gap-1"><TrendingUp size={10} />คะแนนเฉลี่ย</div>
                        <div className="font-semibold text-sm mt-0.5">
                            <ScoreBadge pct={c.avg_score_pct} />
                        </div>
                    </div>

                </div>

                {c.latest_attempt && (
                    <div className="flex items-center gap-1 text-[11px] text-gray-400">
                        <Clock size={11} /> ล่าสุด: {formatDateTime(c.latest_attempt)}
                    </div>
                )}

                <div className="pt-1 flex justify-end">
                    <span className="text-xs text-primary font-semibold flex items-center gap-1 group-hover:underline">
                        ดูรายละเอียด <ChevronRight size={13} />
                    </span>
                </div>
            </div>
        </motion.button>
    )
}

// ─── Layer 2: Course Detail ─────────────────────────────────
function CourseDetail({ learner, course, onBack, onBackToLearner, onViewSubmission }) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [expanded, setExpanded] = useState({})

    const learnerName = learner.display_name || `${learner.first_name || ''} ${learner.last_name || ''}`.trim() || learner.username

    useEffect(() => {
        setLoading(true); setError(null)
        getReportLearnerCourse(learner.id, course.course_id)
            .then(r => setData(r.data || {}))
            .catch(() => setError('โหลดข้อมูลไม่สำเร็จ'))
            .finally(() => setLoading(false))
    }, [learner.id, course.course_id])

    const tests = data?.tests || []

    const toggle = id => setExpanded(p => ({ ...p, [id]: !p[id] }))

    return (
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="flex flex-col gap-5">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors">
                    <ArrowLeft size={15} /> รายชื่อผู้เรียน
                </button>
                <ChevronRight size={14} className="text-gray-300" />
                <button onClick={onBackToLearner} className="text-gray-600 hover:text-primary transition-colors">
                    {learnerName}
                </button>
                <ChevronRight size={14} className="text-gray-300" />
                <span className="text-gray-800 font-medium">{course.course_name}</span>
            </div>

            {/* Course Header */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-primary/10 px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="shrink-0 w-14 h-14 rounded-xl bg-white shadow flex items-center justify-center overflow-hidden">
                        {course.image_url
                            ? <img src={course.image_url} alt={course.course_name} className="w-full h-full object-cover" />
                            : <BookOpen size={24} className="text-primary" />
                        }
                    </div>
                    <div className="flex-1">
                        <div className="text-lg font-semibold text-gray-900">{course.course_name}</div>
                        {course.course_code && <div className="text-sm text-gray-500">รหัส: {course.course_code}</div>}
                    </div>
                    <div className="flex gap-4 text-center">
                        <div>
                            <div className="text-lg font-bold text-primary">{course.tests_attempted ?? 0}/{course.test_count ?? 0}</div>
                            <div className="text-xs text-gray-500">ทำสอบ</div>
                        </div>
                        <div>
                            <div className="text-lg font-bold text-secondary">{pct(course.avg_score_pct)}</div>
                            <div className="text-xs text-gray-500">เฉลี่ย</div>
                        </div>
                        <div>
                            <div className="text-lg font-bold text-gray-800">{course.attempt_count ?? 0}</div>
                            <div className="text-xs text-gray-500">ครั้งสอบ</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tests */}
            {loading ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                    <Loader2 size={24} className="animate-spin mr-2" /> กำลังโหลด...
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-2 text-red-600">
                    <AlertCircle size={18} /> {error}
                </div>
            ) : tests.length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-500">
                    <FileText size={36} className="mx-auto mb-2 opacity-30" />
                    ไม่มีชุดข้อสอบในหลักสูตรนี้
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                        <FileText size={17} className="text-secondary" /> ชุดข้อสอบ ({tests.length} ชุด)
                    </h2>
                    {tests.map((test, idx) => {
                        const isOpen = !!expanded[test.test_id]
                        const runs = test.runs || []
                        const bestRun = runs.length > 0 ? runs.reduce((b, r) => (r.score_pct > (b.score_pct ?? 0) ? r : b), runs[0]) : null
                        const hasPassed = runs.some(r => r.passed)

                        return (
                            <div key={test.test_id || idx} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                {/* Test Header / Toggle */}
                                <button
                                    type="button"
                                    onClick={() => toggle(test.test_id)}
                                    className="w-full flex items-center justify-between px-4 py-4 text-left hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white
                      ${hasPassed ? 'bg-green-500' : runs.length > 0 ? 'bg-red-400' : 'bg-gray-300'}`}>
                                            {idx + 1}
                                        </span>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm text-gray-900 truncate">{test.test_name}</div>
                                            <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                                {test.criteria != null && (
                                                    <span className="text-[11px] text-gray-500 flex items-center gap-0.5">
                                                        <Trophy size={10} className="text-yellow-500" /> เกณฑ์ผ่าน {test.criteria}%
                                                    </span>
                                                )}
                                                <span className="text-[11px] text-gray-500">{runs.length} ครั้งที่สอบ</span>
                                                {bestRun && <ScoreBadge pct={bestRun.score_pct} />}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {hasPassed && <PassBadge passed={true} />}
                                        {!hasPassed && runs.length > 0 && <PassBadge passed={false} />}
                                        {runs.length === 0 && <span className="text-xs text-gray-400">ยังไม่ได้สอบ</span>}
                                        <ChevronRight size={16} className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                                    </div>
                                </button>

                                {/* Runs Table */}
                                <AnimatePresence>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                                                {runs.length === 0 ? (
                                                    <p className="text-sm text-gray-400 py-2">ยังไม่มีประวัติการสอบ</p>
                                                ) : (
                                                    <div className="overflow-x-auto rounded-lg border border-gray-100">
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="bg-gray-50 text-xs text-gray-500 font-medium">
                                                                    <th className="px-3 py-2 text-left">ครั้งที่</th>
                                                                    <th className="px-3 py-2 text-center">คะแนน</th>
                                                                    <th className="px-3 py-2 text-center">เปอร์เซ็นต์</th>
                                                                    <th className="px-3 py-2 text-center">ผล</th>
                                                                    <th className="px-3 py-2 text-right">วันที่ส่ง</th>
                                                                    <th className="px-3 py-2 text-center">ผลการสอบ</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {runs.map((run, ri) => (
                                                                    <tr key={ri} className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
                                                                        <td className="px-3 py-2.5 text-gray-600 font-medium">#{run.run_no ?? ri + 1}</td>
                                                                        <td className="px-3 py-2.5 text-center font-semibold text-gray-800">{fmtScore(run.score, run.full_score)}</td>
                                                                        <td className="px-3 py-2.5 text-center"><ScoreBadge pct={run.score_pct} /></td>
                                                                        <td className="px-3 py-2.5 text-center"><PassBadge passed={run.passed} /></td>
                                                                        <td className="px-3 py-2.5 text-right text-xs text-gray-400">
                                                                            <span className="flex items-center justify-end gap-1">
                                                                                <Calendar size={11} />{formatDateTime(run.submitted_at)}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-3 py-2.5 text-center">
                                                                            {run.submission_id ? (
                                                                                <button
                                                                                    onClick={(e) => { e.stopPropagation(); onViewSubmission(run.submission_id, test.test_name) }}
                                                                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-xs font-semibold transition-colors"
                                                                                >
                                                                                    <Eye size={12} /> รายละเอียด
                                                                                </button>
                                                                            ) : (
                                                                                <span className="text-gray-300 text-xs">-</span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )
                    })}
                </div>
            )}
        </motion.div>
    )
}

// ─── Layer 3: Submission Review ─────────────────────────────
function SubmissionReview({ learner, submissionId, testName, onBack, onBackToLearner, onBackToCourse, courseName }) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const learnerName = learner.display_name || `${learner.first_name || ''} ${learner.last_name || ''}`.trim() || learner.username

    useEffect(() => {
        setLoading(true); setError(null)
        getReportLearnerSubmission(learner.id, submissionId)
            .then(r => setData(r.data || {}))
            .catch(() => setError('โหลดข้อมูลไม่สำเร็จ'))
            .finally(() => setLoading(false))
    }, [learner.id, submissionId])

    const submission = data?.submission || {}
    const answers = data?.answers || []

    // helper: get option letter keys from the options array
    const getOptionLabel = (optObj) => {
        if (!optObj || typeof optObj !== 'object') return ''
        const [key] = Object.keys(optObj)
        return key
    }
    const getOptionDesc = (optObj) => {
        if (!optObj || typeof optObj !== 'object') return ''
        const [key] = Object.keys(optObj)
        return optObj[key]?.description ?? ''
    }

    // ─── PDF Export ───────────────────────────────────────────
    const printExamReport = () => {
        const sub = data?.submission || {}
        const ans = data?.answers || []
        const toArr = v => Array.isArray(v) ? v : (v != null && v !== '' ? [v] : [])
        const norm = s => (s || '').toLowerCase()
        const keyToLetter = key => { const m = (key || '').match(/\d+/); return m ? String.fromCharCode(64 + parseInt(m[0])) : key }
        const now = new Date()
        const thDate = now.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })
        const thTime = now.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
        const fullName = `${learner.first_name || ''} ${learner.last_name || ''}`.trim() || learner.display_name || learner.username || '-'

        const questionsHtml = ans.map((a, qi) => {
            const selected = toArr(a.selected)
            const correct = toArr(a.correct)
            const opts = (a.options || []).map((optObj, oi) => {
                if (!optObj || typeof optObj !== 'object') return ''
                const [rawKey] = Object.keys(optObj)
                const letter = keyToLetter(rawKey)
                const desc = optObj[rawKey]?.description ?? ''
                const isSel = selected.some(s => norm(s) === norm(rawKey))
                const isCorr = correct.some(c => norm(c) === norm(rawKey))
                let bg = ''
                let mark = ''
                if (isSel && isCorr) { bg = 'background:#d1fae5;'; mark = ' ✓' }
                else if (isCorr && !isSel) { bg = 'background:#dbeafe;'; mark = ' ✓' }
                else if (isSel && !isCorr) { bg = 'background:#fee2e2;'; mark = ' ✗' }
                return `<div style="display:flex;align-items:flex-start;gap:8px;padding:5px 8px;border-radius:6px;margin-bottom:4px;${bg}">
                    <span style="min-width:24px;height:24px;border-radius:50%;border:1.5px solid #374151;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0;">${letter}</span>
                    <span style="font-size:13px;line-height:1.5;">${desc}${mark}</span>
                </div>`
            }).join('')
            const selLetters = selected.map(s => keyToLetter(s)).join(', ') || '-'
            const corrLetters = correct.map(c => keyToLetter(c)).join(', ')
            const isCorrect = a.choice?.is_correct ?? (a.awarded > 0)
            return `<div style="margin-bottom:20px;page-break-inside:avoid;">
                <div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:8px;">
                    <span style="min-width:28px;height:28px;border-radius:50%;background:${isCorrect ? '#22c55e' : '#f87171'};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;margin-top:1px;">${a.no}</span>
                    <div style="flex:1;">
                        <p style="margin:0 0 8px;font-size:13.5px;font-weight:600;line-height:1.6;">${a.question}</p>
                        <div>${opts}</div>
                        <div style="margin-top:6px;padding:4px 10px;border-radius:6px;border:1px solid #e5e7eb;background:#f9fafb;display:flex;gap:24px;font-size:11px;color:#6b7280;">
                            <span>คำตอบที่เลือก: <strong style="color:#374151">${selLetters}</strong></span>
                            <span>คำตอบที่ถูก: <strong style="color:#15803d">${corrLetters}</strong></span>
                            <span>คะแนน: <strong style="color:#374151">${a.awarded ?? 0}/${a.max_points ?? 1}</strong></span>
                        </div>
                    </div>
                </div>
            </div>`
        }).join('')

        const html = `<!DOCTYPE html><html lang="th"><head><meta charset="UTF-8">
        <title>ข้อสอบ - ${sub.test_name || testName}</title>
        <style>
            @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700&display=swap');
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Sarabun', 'TH Sarabun New', sans-serif; font-size: 14px; color: #1f2937; background: #fff; padding: 0; }
            .page { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 15mm 20mm; }
            .header { text-align: center; border-bottom: 3px double #1e40af; padding-bottom: 12px; margin-bottom: 16px; }
            .header h1 { font-size: 20px; font-weight: 700; color: #1e3a8a; letter-spacing: 0.5px; }
            .header h2 { font-size: 16px; font-weight: 600; color: #1e40af; margin-top: 4px; }
            .header p { font-size: 13px; color: #4b5563; margin-top: 4px; }
            .info-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 13px; }
            .info-table td { padding: 5px 8px; border: 1px solid #d1d5db; }
            .info-table .label { font-weight: 600; width: 28%; background: #eff6ff; }
            .score-box { display: flex; justify-content: flex-end; margin-bottom: 14px; }
            .score-inner { border: 2px solid #1e40af; border-radius: 10px; padding: 10px 24px; text-align: center; min-width: 180px; }
            .score-inner .score-num { font-size: 28px; font-weight: 700; color: #1e3a8a; }
            .score-inner .score-label { font-size: 12px; color: #6b7280; margin-top: 2px; }
            .score-inner .pass-badge { display:inline-block; margin-top:6px; padding: 2px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; background: ${sub.passed ? '#dcfce7' : '#fee2e2'}; color: ${sub.passed ? '#15803d' : '#b91c1c'}; }
            .instructions { background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 10px 14px; margin-bottom: 16px; font-size: 12.5px; color: #78350f; }
            .instructions strong { display: block; margin-bottom: 4px; font-size: 13px; color: #92400e; }
            .section-title { font-size: 14px; font-weight: 700; color: #1e3a8a; border-left: 4px solid #1e40af; padding-left: 10px; margin-bottom: 14px; }
            .questions { }
            .footer { border-top: 2px solid #1e40af; padding-top: 10px; margin-top: 20px; display: flex; justify-content: space-between; font-size: 11px; color: #6b7280; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .page { padding: 10mm 15mm; } }
        </style></head><body>
        <div class="page">
            <div class="header">
                <h1>แบบทดสอบ / ผลการสอบ</h1>
                <h2>${sub.test_name || testName}</h2>
                ${sub.course_name ? `<p>วิชา: ${sub.course_name}</p>` : ''}
            </div>

            <div style="display:flex;gap:16px;align-items:flex-start;margin-bottom:14px;">
                <table class="info-table" style="flex:1;margin-bottom:0;">
                    <tr><td class="label">ชื่อ - นามสกุล</td><td>${fullName}</td></tr>
                    ${learner.email ? `<tr><td class="label">อีเมล</td><td>${learner.email}</td></tr>` : ''}
                    ${sub.course_name ? `<tr><td class="label">วิชา / หลักสูตร</td><td>${sub.course_name}</td></tr>` : ''}
                    <tr><td class="label">ชุดข้อสอบ</td><td>${sub.test_name || testName}</td></tr>
                    <tr><td class="label">วันที่สอบ</td><td>${thDate}</td></tr>
                    <tr><td class="label">เวลาสอบ</td><td>${thTime} น.</td></tr>
                    ${sub.spend_sec != null ? `<tr><td class="label">เวลาที่ใช้</td><td>${fmtTime(sub.spend_sec)}</td></tr>` : ''}
                    ${sub.criteria != null ? `<tr><td class="label">เกณฑ์ผ่าน</td><td>${sub.criteria}%</td></tr>` : ''}
                </table>
                <div class="score-box" style="margin:0;">
                    <div class="score-inner">
                        <div class="score-num">${sub.score ?? '-'}/${sub.full_score ?? '-'}</div>
                        <div class="score-label">คะแนนที่ได้ / คะแนนเต็ม</div>
                        ${sub.score_pct != null ? `<div style="font-size:16px;font-weight:700;color:#374151;margin-top:4px;">${Number(sub.score_pct).toFixed(1)}%</div>` : ''}
                        <div class="pass-badge">${sub.passed ? 'ผ่าน' : 'ไม่ผ่าน'}</div>
                    </div>
                </div>
            </div>

            <div class="instructions">
                <strong>คำชี้แจง / Instructions</strong>
                แบบทดสอบฉบับนี้เป็นข้อสอบปรนัยแบบเลือกตอบ ให้เลือกคำตอบที่ถูกต้องที่สุดเพียงข้อเดียว (เว้นแต่จะระบุไว้เป็นอย่างอื่น)
                ตัวเลือกที่เน้นด้วยสีเขียว <span style="background:#d1fae5;padding:1px 6px;border-radius:4px;">✓</span> คือคำตอบที่เลือกและถูกต้อง
                สีน้ำเงิน <span style="background:#dbeafe;padding:1px 6px;border-radius:4px;">✓</span> คือคำตอบที่ถูกต้อง (แต่ไม่ได้เลือก)
                สีแดง <span style="background:#fee2e2;padding:1px 6px;border-radius:4px;">✗</span> คือคำตอบที่เลือกแต่ผิด
            </div>

            <div class="section-title">คำถามและคำตอบ (${ans.length} ข้อ)</div>
            <div class="questions">${questionsHtml}</div>

            <div class="footer">
                <span>พิมพ์เมื่อ: ${thDate} เวลา ${thTime} น.</span>
                <span>ผู้สอบ: ${fullName}</span>
            </div>
        </div>
        <script>window.onload = () => { window.print(); }<\/script>
        </body></html>`

        const w = window.open('', '_blank', 'width=900,height=700')
        if (w) { w.document.write(html); w.document.close() }
    }

    return (
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="flex flex-col gap-5">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors">
                    <ArrowLeft size={15} /> รายชื่อผู้เรียน
                </button>
                <ChevronRight size={14} className="text-gray-300" />
                <button onClick={onBackToLearner} className="text-gray-600 hover:text-primary transition-colors">{learnerName}</button>
                <ChevronRight size={14} className="text-gray-300" />
                <button onClick={onBackToCourse} className="text-gray-600 hover:text-primary transition-colors">{courseName}</button>
                <ChevronRight size={14} className="text-gray-300" />
                <span className="text-gray-800 font-medium truncate max-w-[180px]">{submission.test_name || testName}</span>
            </div>

            {/* Summary Card */}
            {!loading && !error && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-5">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <div className="flex-1">
                                <div className="text-lg font-semibold text-gray-900">{submission.test_name || testName}</div>
                                {submission.course_name && <div className="text-sm text-gray-500 mt-0.5">{submission.course_name}</div>}
                            </div>
                            <div className="flex flex-wrap gap-3 items-stretch">
                                {/* Score */}
                                <div className="bg-white rounded-xl px-4 py-2.5 text-center shadow-sm min-w-[96px] flex flex-col justify-between">
                                    <div className="text-xs text-gray-500">คะแนนที่ได้</div>
                                    <div className="text-2xl font-bold text-indigo-600 mt-1">{fmtScore(submission.score, submission.full_score)}</div>
                                </div>
                                {/* Pct */}
                                <div className="bg-white rounded-xl px-4 py-2.5 text-center shadow-sm min-w-[96px] flex flex-col justify-between">
                                    <div className="text-xs text-gray-500">เปอร์เซ็นต์ที่ได้</div>
                                    <div className={`text-2xl font-bold mt-1 ${submission.score_pct >= (submission.criteria ?? 60) ? 'text-green-600' : 'text-red-500'}`}>
                                        {pct(submission.score_pct)}
                                    </div>
                                </div>
                                {/* Pass */}
                                <div className="bg-white rounded-xl px-4 py-2.5 text-center shadow-sm min-w-[96px] flex flex-col justify-between">
                                    <div className="text-xs text-gray-500">ผลสอบ</div>
                                    <div className="flex items-center justify-center mt-1">
                                        <span className={`text-xl font-bold ${submission.passed ? 'text-green-600' : 'text-red-500'}`}>
                                            {submission.passed ? 'ผ่าน' : 'ไม่ผ่าน'}
                                        </span>
                                    </div>
                                </div>
                                {/* Time */}
                                {submission.spend_sec != null && (
                                    <div className="bg-white rounded-xl px-4 py-2.5 text-center shadow-sm min-w-[96px] flex flex-col justify-between">
                                        <div className="text-xs text-gray-500">เวลาที่ใช้</div>
                                        <div className="text-xl font-bold text-gray-700 flex items-center justify-center gap-1 mt-1">
                                            <Timer size={16} className="text-gray-400" />{fmtTime(submission.spend_sec)}
                                        </div>
                                    </div>
                                )}
                                {/* Export Button */}
                                {answers.length > 0 && (
                                    <button
                                        onClick={printExamReport}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow transition-colors self-center"
                                    >
                                        <Printer size={15} /> Export PDF
                                    </button>
                                )}
                            </div>
                        </div>
                        {submission.criteria != null && (
                            <div className="mt-3 flex items-center gap-2">
                                <Trophy size={13} className="text-yellow-500" />
                                <span className="text-xs text-gray-500">เกณฑ์ผ่าน: <span className="font-semibold text-gray-700">{submission.criteria}%</span></span>
                                <ProgressBar value={submission.score_pct ?? 0} max={100} color={submission.passed ? 'bg-green-500' : 'bg-red-400'} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Questions */}
            {loading ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                    <Loader2 size={24} className="animate-spin mr-2" /> กำลังโหลดข้อสอบ...
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-2 text-red-600">
                    <AlertCircle size={18} /> {error}
                </div>
            ) : answers.length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-400">
                    <HelpCircle size={36} className="mx-auto mb-2 opacity-30" />
                    ไม่พบข้อมูลคำถาม
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                        <FileText size={17} className="text-indigo-500" /> คำถามทั้งหมด ({answers.length} ข้อ)
                    </h2>
                    {answers.map((ans) => {
                        const toArr = v => Array.isArray(v) ? v : (v != null && v !== '' ? [v] : [])
                        const selected = toArr(ans.selected)
                        const correct = toArr(ans.correct)
                        // Normalize OPTION1/option1 → lowercase for comparison
                        const norm = s => (s || '').toLowerCase()
                        // Extract display letter from option key: option1→A, option2→B...
                        const keyToLetter = key => {
                            const m = (key || '').match(/\d+/)
                            return m ? String.fromCharCode(64 + parseInt(m[0])) : key
                        }
                        // key-to-letter for values like OPTION1 → A
                        const valToLetter = val => keyToLetter(val)
                        const isCorrect = ans.awarded > 0 || (ans.choice?.is_correct ?? false)
                        const options = ans.options || []

                        return (
                            <div
                                key={ans.no}
                                className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isCorrect ? 'border-green-200' : 'border-red-200'}`}
                            >
                                {/* Question header */}
                                <div className={`px-4 py-3 flex items-start gap-3 ${isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
                                    <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white mt-0.5
                                        ${isCorrect ? 'bg-green-500' : 'bg-red-400'}`}>
                                        {ans.no}
                                    </span>
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-800 font-medium leading-relaxed">{ans.question}</p>
                                        <div className="flex items-center gap-3 mt-1.5">
                                            <span className="text-xs text-gray-500">
                                                คะแนน: <span className="font-semibold text-gray-700">{ans.awarded ?? 0}/{ans.max_points ?? 1}</span>
                                            </span>
                                            {isCorrect
                                                ? <span className="flex items-center gap-0.5 text-xs text-green-600 font-semibold"><Check size={12} /> ถูก</span>
                                                : <span className="flex items-center gap-0.5 text-xs text-red-500 font-semibold"><X size={12} /> ผิด</span>
                                            }
                                        </div>
                                    </div>
                                </div>

                                {/* Options */}
                                <div className="px-4 py-3 space-y-2">
                                    {options.map((optObj, oi) => {
                                        const rawKey = getOptionLabel(optObj)   // e.g. "option1"
                                        const displayLetter = keyToLetter(rawKey)  // e.g. "A"
                                        const desc = getOptionDesc(optObj)
                                        const isSelected = selected.some(s => norm(s) === norm(rawKey))
                                        const isCorrectOpt = correct.some(c => norm(c) === norm(rawKey))

                                        let optStyle = 'border-gray-100 bg-gray-50 text-gray-700'
                                        let badgeEl = null

                                        if (isCorrectOpt && isSelected) {
                                            optStyle = 'border-green-300 bg-green-50 text-green-800'
                                            badgeEl = <span className="ml-auto flex items-center gap-0.5 text-[10px] font-bold text-green-600 shrink-0"><CheckCircle2 size={12} /> เลือก/ถูก</span>
                                        } else if (isCorrectOpt && !isSelected) {
                                            optStyle = 'border-blue-200 bg-blue-50 text-blue-800'
                                            badgeEl = <span className="ml-auto flex items-center gap-0.5 text-[10px] font-bold text-blue-600 shrink-0"><CheckCircle2 size={12} /> คำตอบที่ถูก</span>
                                        } else if (!isCorrectOpt && isSelected) {
                                            optStyle = 'border-red-200 bg-red-50 text-red-700'
                                            badgeEl = <span className="ml-auto flex items-center gap-0.5 text-[10px] font-bold text-red-500 shrink-0"><XCircle size={12} /> คำตอบที่เลือก</span>
                                        }

                                        return (
                                            <div
                                                key={oi}
                                                className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors ${optStyle}`}
                                            >
                                                <span className="shrink-0 w-6 h-6 rounded-full border border-current flex items-center justify-center text-xs font-bold opacity-70">
                                                    {displayLetter}
                                                </span>
                                                <span className="flex-1 leading-snug">{desc}</span>
                                                {badgeEl}
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* Answer summary row */}
                                <div className="px-4 pb-3 flex flex-wrap gap-4 text-xs text-gray-500">
                                    <span>
                                        คำตอบที่เลือก:{' '}
                                        {selected.length > 0
                                            ? selected.map(s => <span key={s} className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-semibold mr-1">{valToLetter(s)}</span>)
                                            : <span className="text-gray-400">-</span>
                                        }
                                    </span>
                                    <span>
                                        คำตอบถูก:{' '}
                                        {correct.map(c => <span key={c} className="inline-block px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-semibold mr-1">{valToLetter(c)}</span>)}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </motion.div>
    )
}

// ─── Root Component ─────────────────────────────────────────
export default function ReportLearnerPage() {
    const [view, setView] = useState('list')          // 'list' | 'learner' | 'course' | 'submission'
    const [selectedLearner, setSelectedLearner] = useState(null)
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [selectedSubmission, setSelectedSubmission] = useState(null) // { submission_id, test_name }

    const handleSelectLearner = learner => {
        setSelectedLearner(learner)
        setSelectedCourse(null)
        setSelectedSubmission(null)
        setView('learner')
    }

    const handleSelectCourse = course => {
        setSelectedCourse(course)
        setSelectedSubmission(null)
        setView('course')
    }

    const handleViewSubmission = (submission_id, test_name) => {
        setSelectedSubmission({ submission_id, test_name })
        setView('submission')
    }

    const goList = () => { setView('list'); setSelectedLearner(null); setSelectedCourse(null); setSelectedSubmission(null) }
    const goLearner = () => { setView('learner'); setSelectedCourse(null); setSelectedSubmission(null) }
    const goCourse = () => { setView('course'); setSelectedSubmission(null) }

    return (
        <div className="sm:p-6 mx-auto">
            <AnimatePresence mode="sync">
                {view === 'list' && (
                    <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <LearnerList onSelect={handleSelectLearner} />
                    </motion.div>
                )}
                {view === 'learner' && selectedLearner && (
                    <motion.div key="learner" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <LearnerOverview
                            learner={selectedLearner}
                            onSelectCourse={handleSelectCourse}
                            onBack={goList}
                        />
                    </motion.div>
                )}
                {view === 'course' && selectedLearner && selectedCourse && (
                    <motion.div key="course" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <CourseDetail
                            learner={selectedLearner}
                            course={selectedCourse}
                            onBack={goList}
                            onBackToLearner={goLearner}
                            onViewSubmission={handleViewSubmission}
                        />
                    </motion.div>
                )}
                {view === 'submission' && selectedLearner && selectedCourse && selectedSubmission && (
                    <motion.div key="submission" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <SubmissionReview
                            learner={selectedLearner}
                            submissionId={selectedSubmission.submission_id}
                            testName={selectedSubmission.test_name}
                            courseName={selectedCourse.course_name}
                            onBack={goList}
                            onBackToLearner={goLearner}
                            onBackToCourse={goCourse}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
